import {Controller} from "stimulus"

/**
 * Stimulus controller for goal list functionality:
 * - Show More: reveals 5 more goals each click
 * - Sorting: reorders goals by priority, date, etc.
 * - Checkbox completion: animates and moves goals between incomplete/completed lists
 *
 * Usage in Hiccup:
 *   [:div {:data-controller "fwa-goal-list"
 *          :data-fwa-goal-list-page-size "5"
 *          :data-fwa-goal-list-type "incomplete"}  ;; or "completed"
 *    [:div {:data-target "fwa-goal-list.list"}
 *      [:div {:data-target "fwa-goal-list.item"} ...]
 *    ]
 *    [:button {:data-action "click->fwa-goal-list#showMore"} "Show More"]]
 */
export default class extends Controller {
  static get targets() {
    return ["list", "item", "showMoreBtn", "sortBtn", "sortMenu", "sortOption", "checkbox", "emptyState"]
  }

  connect() {
    this.pageSize = parseInt(this.element.getAttribute("data-fwa-goal-list-page-size") || "5", 10)
    this.visibleCount = this.pageSize
    this.currentSort = this.element.getAttribute("data-fwa-goal-list-sort") || "priority"
    this.listType = this.element.getAttribute("data-fwa-goal-list-type") || "incomplete"
    this.sortMenuOpen = false

    this.updateVisibility()

    this._boundHandleAnswersSubmitted = this._handleAnswersSubmitted.bind(this)
    document.addEventListener("fwa-goal-answers-submitted", this._boundHandleAnswersSubmitted)

    this._boundLock = () => this.element.classList.add("fwa-goal-list-locked")
    this._boundUnlock = () => this.element.classList.remove("fwa-goal-list-locked")
    document.addEventListener("fwa-goal-saving-started", this._boundLock)
    document.addEventListener("fwa-goal-saving-finished", this._boundUnlock)
    document.addEventListener("fwa-goal-saving-failed", this._boundUnlock)
  }

  disconnect() {
    if (this._boundHandleAnswersSubmitted) {
      document.removeEventListener("fwa-goal-answers-submitted", this._boundHandleAnswersSubmitted)
      this._boundHandleAnswersSubmitted = null
    }
    if (this._boundLock) {
      document.removeEventListener("fwa-goal-saving-started", this._boundLock)
      this._boundLock = null
    }
    if (this._boundUnlock) {
      document.removeEventListener("fwa-goal-saving-finished", this._boundUnlock)
      document.removeEventListener("fwa-goal-saving-failed", this._boundUnlock)
      this._boundUnlock = null
    }
  }

  /**
   * Handle checkbox click - complete/uncomplete a goal.
   * If the goal has a modal-config and is being checked, suppress the toggle
   * and open the modal first. The toggle happens after modal submission.
   */
  toggleCheckbox(event) {
    event.preventDefault()
    event.stopPropagation()

    const checkbox = event.currentTarget
    const itemWrapper = checkbox.closest("[data-target='fwa-goal-list.item']")
    if (!itemWrapper) return

    const goalId = checkbox.getAttribute("data-goal-id")
    const runUuid = checkbox.getAttribute("data-run-uuid")
    const wasChecked = checkbox.getAttribute("data-checked") === "true"
    const nowChecked = !wasChecked
    const modalConfig = checkbox.getAttribute("data-modal-config")

    // If checking a goal with additional questions, open modal instead of toggling
    if (nowChecked && modalConfig) {
      document.dispatchEvent(
        new CustomEvent("fwa-goal-completed", {
          detail: {config: modalConfig, triggerElement: checkbox, goalId, runUuid}
        })
      )
      return // Do NOT toggle yet — modal submission will trigger the toggle
    }

    // No modal needed (unchecking, or no additional questions) — toggle immediately
    this._performToggle(checkbox, itemWrapper, goalId, runUuid, wasChecked, nowChecked)
  }

  /**
   * Perform the actual goal toggle: flip checkbox visual, mark item as saving,
   * lock all goal lists, fire the server request. Item stays in its current
   * list; the server-rendered goals-html replaces the container on success.
   */
  _performToggle(checkbox, itemWrapper, goalId, runUuid, wasChecked, nowChecked, options = {}) {
    // Another toggle is already in flight — peer controllers signal this via
    // the .fwa-goal-list-locked class. Bail to prevent two concurrent
    // /toggle-goal fetches. The user can retry once the in-flight save
    // completes and the new container swap clears the lock.
    if (document.querySelector(".fwa-goal-list-locked")) {
      return
    }
    // Optimistic visual on the checkbox only (so the user sees their click).
    checkbox.setAttribute("data-checked", nowChecked.toString())
    this._updateCheckboxSvg(checkbox, nowChecked)

    // Celebration animation on checkbox
    checkbox.classList.add("fwa-checkbox-pop")
    setTimeout(() => checkbox.classList.remove("fwa-checkbox-pop"), 300)

    // Mark this item as saving and lock all goal lists
    this._setItemSaving(itemWrapper, true)
    document.dispatchEvent(new CustomEvent("fwa-goal-saving-started"))

    if (checkbox.hasAttribute("data-mock")) {
      // Dev-only mock path: simulate a successful server response so the UI
      // lock releases.
      this._setItemSaving(itemWrapper, false)
      document.dispatchEvent(new CustomEvent("fwa-goal-saving-finished"))
      return
    }

    this._saveGoalState(runUuid, goalId, nowChecked, wasChecked, checkbox, itemWrapper, options.answers, options.debts)
  }

  /**
   * Toggle the per-item saving overlay class. CSS handles the spinner +
   * dimming. Called from _performToggle (true) and the success/failure
   * paths (false on failure; on success the whole container is replaced).
   */
  _setItemSaving(itemWrapper, saving) {
    if (!itemWrapper) return
    if (saving) {
      itemWrapper.classList.add("fwa-goal-saving")
      itemWrapper.setAttribute("aria-busy", "true")
    } else {
      itemWrapper.classList.remove("fwa-goal-saving")
      itemWrapper.removeAttribute("aria-busy")
    }
  }

  /**
   * Handle answers submitted from the modal — now perform the actual goal toggle.
   */
  _handleAnswersSubmitted(event) {
    const {goalId, runUuid, answers, debts} = event.detail || {}
    if (!goalId || !runUuid) return

    const checkbox = document.querySelector(`[data-goal-id="${goalId}"][data-run-uuid="${runUuid}"]`)
    if (!checkbox) return

    const itemWrapper = checkbox.closest("[data-target='fwa-goal-list.item']")
    if (!itemWrapper) return

    const wasChecked = checkbox.getAttribute("data-checked") === "true"
    this._performToggle(checkbox, itemWrapper, goalId, runUuid, wasChecked, true, {answers, debts})
  }

  /**
   * Update the checkbox SVG based on checked state
   */
  _updateCheckboxSvg(checkbox, isChecked) {
    if (isChecked) {
      checkbox.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 22C6.475 22 2 17.525 2 12C2 6.475 6.475 2 12 2C17.525 2 22 6.475 22 12C22 17.525 17.525 22 12 22ZM7.3 13.2L9.8 15.7C10 15.9 10.25 16 10.5 16C10.75 16 11 15.9 11.2 15.7L16.7 10.2C17.1 9.825 17.1 9.175 16.7 8.8C16.325 8.4 15.675 8.4 15.3 8.8L10.5 13.575L8.7 11.8C8.325 11.4 7.675 11.4 7.3 11.8C6.9 12.175 6.9 12.825 7.3 13.2Z" fill="#219C11"/></svg>`
    } else {
      checkbox.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6.5 21.5C4.3 21.5 2.5 19.7 2.5 17.5V6.5C2.5 4.3 4.3 2.5 6.5 2.5H17.5C19.7 2.5 21.5 4.3 21.5 6.5V17.5C21.5 19.7 19.7 21.5 17.5 21.5H6.5ZM4.5 17.5C4.5 18.6 5.4 19.5 6.5 19.5H17.5C18.6 19.5 19.5 18.6 19.5 17.5V6.5C19.5 5.4 18.6 4.5 17.5 4.5H6.5C5.4 4.5 4.5 5.4 4.5 6.5V17.5Z" fill="#1D1726"/></svg>`
    }
  }

  /**
   * Check if debug mode is active
   */
  _isDebugMode() {
    // Check URL param
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("debug") === "1") {
      return true
    }

    // Check data attribute
    const pageEl = document.querySelector("[data-debug='true']")
    return !!pageEl
  }

  /**
   * Save goal state to server and update all score displays with the response.
   * Also replaces the goals container with freshly server-rendered HTML so
   * priorities and visibility reflect the updated user profile.
   */
  _saveGoalState(runUuid, goalId, isChecked, wasChecked, checkbox, itemWrapper, answers, debts) {
    const activeCategory = document.querySelector("[data-fwa-active-category]")?.getAttribute("data-fwa-active-category") ?? null

    const body = {
      "run-uuid": runUuid,
      "goal-id": parseInt(goalId, 10),
      "checked": isChecked,
      "category": activeCategory,
      "debug": this._isDebugMode()
    }

    if (answers && answers.length > 0) body.answers = answers
    if (debts) body.debts = debts

    fetch("/api4/wellness/fwa/toggle-goal", {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(body)
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) {
          console.warn("Failed to update goal on server, reverting")
          this._revertOptimisticToggle(checkbox, wasChecked, itemWrapper)
          return
        }
        if (data["score-change"] != null || data["goals-finished"] != null) {
          const scoreChange = data["score-change"] ?? 0
          const isPositive = scoreChange > 0
          const isNegative = scoreChange < 0
          const sign = isPositive ? "+" : ""
          const greenColor = "var(--fwa-success-green)"
          const redColor = "var(--fwa-error-red, #e90000)"
          const color = isPositive ? greenColor : isNegative ? redColor : "black"

          if (data["goals-finished"] != null) {
            document.querySelectorAll("[data-fwa-goals-finished]").forEach((el) => {
              el.textContent = data["goals-finished"]
              el.style.color = isPositive ? greenColor : "black"
            })
          }
          // Always render the value with sign + %, colored to match the
          // server-rendered initial state (fwa_dashboard_panels.clj).
          document.querySelectorAll("[data-fwa-score-change-value]").forEach((el) => {
            el.textContent = `${sign}${scoreChange}%`
            el.style.color = color
            el.style.display = ""
          })
        }
        if (data["score"] != null) {
          document.querySelectorAll("[data-fwa-score-display]").forEach((el) => {
            el.textContent = data["score"]
          })
          document.querySelectorAll("[data-fwa-header-score]").forEach((el) => {
            el.textContent = data["score"]
          })
          document.querySelectorAll("[data-fwa-home-copy-score]").forEach((el) => {
            el.textContent = `${data["score"]}/100`
          })
          this._updateHeaderArc(data["score"])
          // Slide the scorebar handle to the new position. Mirrors the SSR
          // clamp in fwa_dashboard.clj/ScorebarSection + title-and-scorebar
          // (score outside [0, 100] falls back to 0).
          const score = data["score"]
          const scorePos = typeof score === "number" && score >= 0 && score <= 100 ? score : 0
          document.querySelectorAll("[data-fwa-scorebar-indicator]").forEach((el) => {
            el.style.left = `${scorePos}%`
          })
        }
        if (data["score-band-label"] != null) {
          document.querySelectorAll("[data-fwa-score-label]").forEach((el) => {
            el.textContent = data["score-band-label"]
          })
          document.querySelectorAll("[data-fwa-home-copy-trigger]").forEach((el) => {
            el.textContent = data["score-band-label"]
            el.setAttribute("aria-label", `View details for ${data["score-band-label"]} category`)
            el.setAttribute("data-score-category-title", data["score-band-label"])
            if (data["score-band-description"] != null) {
              el.setAttribute("data-score-category-description", data["score-band-description"])
            }
            if (data["score-band-dot-color"] != null) {
              el.setAttribute("data-score-category-dot-color", data["score-band-dot-color"])
            }
          })
        }
        if (data["score-band-color-class"] != null) {
          document.querySelectorAll("[data-fwa-scorebar-indicator]").forEach((el) => {
            el.classList.forEach((c) => {
              if (c.startsWith("fwa-score-")) el.classList.remove(c)
            })
            el.classList.add(data["score-band-color-class"])
          })
        }
        if (data.scores) {
          this._applyScoreUpdates(data.scores)
        }
        if (data["profile-diff"] && Array.isArray(data["profile-diff"]) && data["profile-diff"].length > 0) {
          console.debug("[FWA Goal List] Received profile diff:", data["profile-diff"])
          document.dispatchEvent(
            new CustomEvent("fwa-profile-diff-ready", {
              detail: {diffData: data["profile-diff"]}
            })
          )
        }
        // Sync swap: dispatch fwa-goal-saving-finished first to unlock peer
        // controllers (their _boundUnlock fires synchronously on their
        // still-attached root), then replace the container HTML. Stimulus'
        // MutationObserver tears down the old controllers and connects fresh
        // ones on the new DOM in the next microtask.
        if (data["goals-html"]) {
          const container = document.querySelector("[data-fwa-goals-container]")
          if (container) {
            document.dispatchEvent(new CustomEvent("fwa-goal-saving-finished"))
            container.innerHTML = data["goals-html"]
          } else {
            // Defensive: no container to swap into. Release the lock manually
            // and surface as a failure so any future error consumers see it.
            document.dispatchEvent(new CustomEvent("fwa-goal-saving-failed"))
          }
        } else {
          // Server responded success=true but didn't send HTML. Release the
          // lock and clear the per-item state; treat the missing HTML as a
          // server-side failure so the UI doesn't get stuck.
          this._setItemSaving(itemWrapper, false)
          document.dispatchEvent(new CustomEvent("fwa-goal-saving-failed"))
        }
      })
      .catch((error) => {
        console.error("Error updating goal:", error)
        this._revertOptimisticToggle(checkbox, wasChecked, itemWrapper)
      })
  }

  /**
   * Revert the checkbox visual on failure, clear the saving overlay, and
   * release the global lock. The item never moved between lists, so no DOM
   * surgery is needed.
   */
  _revertOptimisticToggle(checkbox, previousCheckedState, itemWrapper) {
    checkbox.setAttribute("data-checked", previousCheckedState.toString())
    this._updateCheckboxSvg(checkbox, previousCheckedState)
    this._setItemSaving(itemWrapper, false)
    document.dispatchEvent(new CustomEvent("fwa-goal-saving-failed"))
  }

  // Mirror of banzai.fwa-scoring.core.score-band/bands.
  // Thresholds and :dot-color must match the band table at
  // components/fwa-scoring/src/banzai/fwa_scoring/core/score_band.clj.
  // Keep in sync when bands change.
  _getScoreColor(score) {
    if (score < 20) return "#E90000" // Critical
    if (score < 40) return "#FAA907" // At-Risk
    if (score < 60) return "#FCEB03" // Established
    if (score < 80) return "#6FEA5F" // Secure
    return "#219C11" // Thriving
  }

  /**
   * Apply recalculated per-category scores to navigation donuts.
   * The overall scorebar number, label, color, and header donut are now
   * driven by the server-supplied `score`, `score-band-label`, and
   * `score-band-color-class` fields handled inline in _saveGoalState.
   */
  _applyScoreUpdates(scores) {
    // Build a UI-category-keyed map of rounded scores
    const uiScores = {}
    Object.entries(scores).forEach(([dbCat, score]) => {
      if (score != null) {
        uiScores[dbCat] = Math.round(score)
      }
    })

    const NAV_DONUT_CIRCUMFERENCE = 2 * Math.PI * 13 // matches fwa_dashboard.clj/navigation-item donut (r=13)

    // Update each navigation-item donut badge (number + arc + color)
    Object.entries(uiScores).forEach(([uiCat, score]) => {
      const color = this._getScoreColor(score)
      const dasharray = `${((score * NAV_DONUT_CIRCUMFERENCE) / 100).toFixed(2)} ${NAV_DONUT_CIRCUMFERENCE}`

      document.querySelectorAll(`[data-fwa-nav-score="${uiCat}"]`).forEach((el) => {
        el.textContent = score
      })
      document.querySelectorAll(`[data-fwa-nav-arc="${uiCat}"]`).forEach((el) => {
        el.setAttribute("stroke-dasharray", dasharray)
        el.setAttribute("stroke", color)
      })
    })
  }

  /**
   * Update the header donut arc(s) (stroke color + length) to reflect a new
   * score. Reads each tagged circle's own radius so both the small (r=14) and
   * pill (r=18.5) donuts get the correct arc. Mirrors server-side geometry in
   * common.clj (circumferences 87.96 for r=14, 116.24 for r=18.5).
   */
  _updateHeaderArc(score) {
    const color = this._getScoreColor(score)
    document.querySelectorAll("[data-fwa-header-arc]").forEach((el) => {
      const r = parseFloat(el.getAttribute("r")) || 18.5
      const circumference = 2 * Math.PI * r
      const dasharray = `${((score * circumference) / 100).toFixed(2)} ${circumference}`
      el.setAttribute("stroke-dasharray", dasharray)
      el.setAttribute("stroke", color)
    })
  }

  /**
   * Show 5 more items
   */
  showMore(event) {
    if (event) {
      event.preventDefault()
    }
    this.visibleCount += this.pageSize
    this.updateVisibility()
  }

  /**
   * Update which items are visible based on visibleCount
   */
  updateVisibility() {
    const items = this.hasItemTarget ? this.itemTargets : []
    items.forEach((item, index) => {
      if (index < this.visibleCount) {
        item.style.display = ""
        item.removeAttribute("hidden")
      } else {
        item.style.display = "none"
        item.setAttribute("hidden", "")
      }
    })

    // Hide/show the Show More button
    if (this.hasShowMoreBtnTarget) {
      if (this.visibleCount >= items.length) {
        this.showMoreBtnTarget.style.display = "none"
      } else {
        this.showMoreBtnTarget.style.display = ""
      }
    }

    // Toggle completed-list empty state if present.
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.style.display = items.length > 0 ? "none" : ""
    }

    // Keep completed section hidden until it has at least one goal.
    if (this.listType === "completed") {
      this.element.style.display = items.length > 0 ? "" : "none"
    }
  }

  /**
   * Toggle sort dropdown menu
   */
  toggleSort(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.sortMenuOpen = !this.sortMenuOpen

    if (this.hasSortMenuTarget) {
      if (this.sortMenuOpen) {
        this.sortMenuTarget.style.display = "block"
        this._clickOutsideHandler = (e) => {
          if (!this.sortMenuTarget.contains(e.target) && !this.sortBtnTarget.contains(e.target)) {
            this.closeSort()
          }
        }
        document.addEventListener("click", this._clickOutsideHandler)
      } else {
        this.closeSort()
      }
    }
  }

  /**
   * Close sort dropdown
   */
  closeSort() {
    this.sortMenuOpen = false
    if (this.hasSortMenuTarget) {
      this.sortMenuTarget.style.display = "none"
    }
    if (this._clickOutsideHandler) {
      document.removeEventListener("click", this._clickOutsideHandler)
      this._clickOutsideHandler = null
    }
  }

  /**
   * Select a sort option
   */
  selectSort(event) {
    if (event) {
      event.preventDefault()
    }

    const sortType = event.currentTarget.getAttribute("data-sort-type")
    if (sortType) {
      this.currentSort = sortType
      this.sortGoals()
      this.updateSortLabel()
      this.closeSort()
    }
  }

  /**
   * Sort goals based on current sort type
   */
  sortGoals() {
    if (!this.hasListTarget || !this.hasItemTarget) return

    const list = this.listTarget
    const items = Array.from(this.itemTargets)

    items.sort((a, b) => {
      switch (this.currentSort) {
        case "priority":
          const priorityA = parseInt(a.getAttribute("data-goal-priority") || "999", 10)
          const priorityB = parseInt(b.getAttribute("data-goal-priority") || "999", 10)
          return priorityA - priorityB

        case "date":
          const dateA = a.getAttribute("data-goal-date") || ""
          const dateB = b.getAttribute("data-goal-date") || ""
          return dateB.localeCompare(dateA)

        case "title":
          const titleA = a.getAttribute("data-goal-title") || ""
          const titleB = b.getAttribute("data-goal-title") || ""
          return titleA.localeCompare(titleB)

        default:
          return 0
      }
    })

    items.forEach((item) => list.appendChild(item))
    this.updateVisibility()
  }

  /**
   * Update the sort button label
   */
  updateSortLabel() {
    if (!this.hasSortBtnTarget) return

    const labelMap = {
      priority: "Top Priority",
      date: "Date Added",
      title: "Alphabetical"
    }

    const labelEl = this.sortBtnTarget.querySelector("[data-sort-label]")
    if (labelEl) {
      labelEl.textContent = "Sort by: " + (labelMap[this.currentSort] || this.currentSort)
    }
  }
}
